<template>
  <el-dialog
    v-model="modalVisible"
    :title="'Nuevo Engrane Troquel'"
    style="width: 45%"
    center
    @closed="handleDialogClosed"
  >
    <div class="detail-container">
      <el-row :gutter="24">
        <custom-select
          v-model="selectedCilindroTipo"
          :label="'Tipo cilindro'"
          :spanColumn="24"
          :settings="{
            placeholder: 'Seleccionar cilindro',
            inputClass: { 'input-error': !validateCylinderAdapterFields.CodigoCilindroTipo },
            options: cilindroTipoList,
          }"
        ></custom-select>
      </el-row>

      <el-row :gutter="24">
        <custom-input-number
          v-model="cylinderAdapter.Dientes"
          :label="'Dientes'"
          :spanColumn="12"
          :settings="{
            placeholder: 'Cantidad de dientes',
            inputClass: ['ancho_total', { 'input-error': !validateCylinderAdapterFields.Dientes }],
            min: 1,
            precision: 0,
            step: 1,
            controls: false,
          }"
        ></custom-input-number>

        <custom-fraction-mm-input
          v-model="cylinderAdapter.Paso"
          :label="'Paso'"
          :spanColumn="12"
          :settings="{
            placeholder: 'Valor en mm o fracción de pulgada (ej: 1/8)',
            inputClass: { 'input-error': !validateCylinderAdapterFields.Paso },
          }"
        ></custom-fraction-mm-input>
      </el-row>
    </div>

    <div class="container">
      <custom-confirm-buttons
        :disabled="false"
        :showLoading="false"
        :confirmText="'Guardar'"
        :cancelText="'Cancelar'"
        @on-cancel="onCancel"
        @on-confirm="onConfirm"
      ></custom-confirm-buttons>
    </div>
  </el-dialog>
</template>

<script setup>
import {
  computed,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElNotification } from 'element-plus';
import { useCilindroTipoStore } from '@/stores/cilindro-tipo/cilindro-tipo';
import { useCylinderAdapterStore } from '../stores/cylinder_adapters_store.js';
import CustomConfirmButtons from '@/shared/components/custom_confirm_buttons.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomFractionMmInput from '@/shared/components/custom_fraction_mm_input.vue';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';

const cilindroTipoStore = useCilindroTipoStore();
const cylinderAdapterStore = useCylinderAdapterStore();

const { cilindroTipoList } = storeToRefs(cilindroTipoStore);
const { cylinderAdapter, validateCylinderAdapterFields } = storeToRefs(cylinderAdapterStore);
const {
  resetCilindroAdaptador,
  validarCampos,
  resetValidations,
  newCilindroAdaptadorHTTP,
  isDuplicateError,
} = cylinderAdapterStore;

const emit = defineEmits(['closed']);
const modalVisible = defineModel({ default: false });

const selectedCilindroTipo = computed({
  get: () =>
    cilindroTipoList.value.find((it) => it.Codigo === cylinderAdapter.value.CodigoCilindroTipo) ||
    null,
  set: (value) => {
    cylinderAdapter.value.CodigoCilindroTipo = value?.Codigo || null;
    cylinderAdapter.value.CilindroTipo = value || null;
  },
});

const closeModal = () => {
  modalVisible.value = false;
};

const handleDialogClosed = () => {
  emit('closed');
};

const onCancel = () => {
  resetCilindroAdaptador();
  resetValidations();
  closeModal();
};

onBeforeMount(async () => {
  await cilindroTipoStore.firstCilindroTipo();
});

const getErrorMessage = () => {
  const errors = [];
  if (!validateCylinderAdapterFields.value.CodigoCilindroTipo) errors.push('Tipo cilindro');
  if (!validateCylinderAdapterFields.value.Dientes) errors.push('Dientes (debe ser mayor a 0)');
  if (!validateCylinderAdapterFields.value.Paso) errors.push('Paso (debe ser mayor a 0)');
  return errors.length > 0
    ? `Campos requeridos: ${errors.join(', ')}`
    : 'Por favor, completar los campos obligatorios';
};

const onConfirm = async () => {
  if (!validarCampos()) {
    ElNotification({
      title: 'Engrane Troquel',
      message: getErrorMessage(),
      type: 'warning',
    });
    return;
  }

  try {
    useLoading().openLoading();
    await newCilindroAdaptadorHTTP();
    useMessageSuccess().open('Los datos se han guardado exitosamente!');
    closeModal();
  } catch (err) {
    if (isDuplicateError(err)) {
      return;
    }
    useMessageError().open(
      err.response?.data?.message || 'Hubo un error al crear el Engrane Troquel',
    );
  } finally {
    useLoading().closeLoading();
  }
};
</script>

<style scoped>
.custom-label {
  font-weight: 700;
  color: #111;
}

.container {
  display: flex;
  justify-content: flex-end;
}
</style>
